import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react'
import { Bookmark, Check, ChevronDown, Copy, Info, Loader2, MoreHorizontal, Share2, Sparkles, Volume2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LANGUAGES, type GenderId, type LangCode, type ModeId, type UsageSnapshot } from '@/lib/baba-api'
import { isDraftableLength } from '@/lib/replyDrafts/api'
import type { TranslatorTab } from './types'
import { scaledFontSize } from './utils'
import { GetOnPhoneNudge } from './TranslatorGetOnPhone'
import { ReplyDraftsSheet } from './ReplyDraftsSheet'
import { WsButtonSecondary, WsIconButton } from './workspace'
import { LimitCard } from './LimitCard'
import type { LimitCardKind } from './planDisplay'
import { MediaResultBody } from '../library/MediaResultView'
import type { MediaResult } from '@/lib/library/mediaResultContent'
import { useSignIn } from '../SignInModal'
import { importWithFreshAssetReload } from '../../lazy-route'

// The markdown parser is a lazy chunk: a result streams in as plain text and
// only a finished one is rendered as markdown (see outputBody below).
const TranslatorMarkdown = lazy(() => importWithFreshAssetReload(() => import('./TranslatorMarkdown')))

/** The result as plain text: what streams, and what shows while the markdown chunk loads. */
function PlainOutput({ text }: { text: string }) {
  return <div className="whitespace-pre-wrap leading-relaxed">{text}</div>
}

export interface TranslatorTargetPaneProps {
  targetLang: LangCode
  sourceLang: LangCode
  mode: ModeId
  tab: TranslatorTab
  isTranslating: boolean
  output: string
  /** A finished image/PDF translation, when the gateway returned the
   *  structured document rather than prose. Rendered through the same view the
   *  History modal uses, so a just-translated PDF shows its pages instead of a
   *  block of text. Takes precedence over `output`, which stays empty in that
   *  case. */
  mediaResult?: MediaResult | null
  /** Key-points summary for the File tab. Empty on the text tab. */
  summary?: string
  /** OCR'd Hebrew original for the Image tab. Empty for Documents/text. */
  originalText?: string
  /** The current source-pane text (the actual translation input). */
  sourceText?: string
  sourceRtl?: boolean
  isPro: boolean
  transliteration: string
  showGenderNudge: boolean
  onDismissGenderNudge: () => void
  onGenderWoman: () => void
  onGenderMan: () => void
  error: string | null
  limitReached: boolean
  /** Which free limit refused the text; renders the inline card. Null for none. */
  limitKind?: LimitCardKind | null
  onUpgradeClick: () => void
  onRetry: () => void
  reverseOutput: string
  isReversing: boolean
  onClearReverse: () => void
  copied: boolean
  saved: boolean
  showCopyMenu: boolean
  setShowCopyMenu: (v: boolean | ((p: boolean) => boolean)) => void
  onCopyFormatted: (variant: 'plain' | 'with-translit' | 'markdown') => void
  isSpeaking: boolean
  /** Clicked, nothing audible yet — generating or fetching the clip. */
  isPreparingSpeech?: boolean
  onSpeak: () => void
  /** Settings → Translation → Auto-Copy, mirrored into the copy menu. */
  autoCopy?: boolean
  onToggleAutoCopy?: (next: boolean) => void
  isProLoaded: boolean
  isAuthed: boolean
  onFavorite: () => void
  onReverseTranslate: () => void
  onShareLink: () => void
  /** Summarize the finished text translation inline (Pro). */
  onSummarizeText?: () => void
  canSummarizeText?: boolean
  retryCount: number
  onRetryWithContext: () => void
  onCancel: () => void
  usage: UsageSnapshot | null
  isSummarizing?: boolean
  translationElapsedMs?: number
  variations?: string[]
  variationsLoading?: boolean
  onGenerateVariations?: () => void
  onPickVariation?: (v: string) => void
  /** Addressee gender for "Reply in Hebrew" (web's raw GenderId — mapped to
   *  the server's vocabulary inside ReplyDraftsSheet). */
  speakingTo?: GenderId
  /** The learner's own gender, for "Reply in Hebrew" first-person forms. */
  selfGender?: GenderId
}

interface MoreMenuItem {
  label: string
  hint?: string
  badge?: string
  onClick: () => void
}

/** Occasional actions live behind one quiet overflow button. */
function MoreMenu({ items }: { items: MoreMenuItem[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (items.length === 0) return null
  return (
    <div ref={ref} className="relative">
      <WsIconButton className="touch-44" icon={<MoreHorizontal size={17} />} label="More actions" size={32} active={open} onClick={() => setOpen((v) => !v)} />
      {open && (
        <div
          role="menu"
          className="absolute bottom-full mb-1.5 left-0 z-40 min-w-[220px] overflow-hidden rounded-md animate-scale-in"
          style={{
            border: '1px solid var(--line)',
            background: 'var(--card)',
            boxShadow: 'var(--shadow-lg)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              role="menuitem"
              onClick={() => { setOpen(false); item.onClick() }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-[13px] transition-colors duration-100 hover:bg-[var(--card-2)] cursor-pointer"
              style={{ color: 'var(--ink)', borderTop: i > 0 ? '1px solid var(--line-hair)' : undefined }}
            >
              <span>
                <span className="block font-medium">{item.label}</span>
                {item.hint && <span className="block text-[11.5px]" style={{ color: 'var(--muted-2)' }}>{item.hint}</span>}
              </span>
              {item.badge && (
                <span className="font-mono text-[9.5px] font-bold px-1.5 py-0.5 rounded shadow-2xs" style={{ background: 'var(--accent)', color: 'var(--cta-fg)' }}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function TranslatorTargetPane({
  targetLang,
  sourceLang,
  mode,
  tab,
  isTranslating,
  output,
  mediaResult = null,
  summary = '',
  originalText = '',
  sourceText = '',
  sourceRtl = false,
  isPro,
  transliteration,
  showGenderNudge,
  onDismissGenderNudge,
  onGenderWoman,
  onGenderMan,
  error,
  limitReached,
  limitKind = null,
  onUpgradeClick,
  onRetry,
  reverseOutput,
  isReversing,
  onClearReverse,
  copied,
  saved,
  showCopyMenu,
  setShowCopyMenu,
  onCopyFormatted,
  isSpeaking,
  isPreparingSpeech,
  onSpeak,
  autoCopy,
  onToggleAutoCopy,
  isProLoaded,
  isAuthed,
  onFavorite,
  onReverseTranslate,
  onShareLink,
  onSummarizeText,
  canSummarizeText = false,
  retryCount,
  onRetryWithContext,
  onCancel,
  isSummarizing = false,
  translationElapsedMs = 0,
  variations = [],
  variationsLoading = false,
  onGenerateVariations,
  onPickVariation,
  speakingTo,
  selfGender,
}: TranslatorTargetPaneProps) {
  const targetRtl = targetLang === 'he' || targetLang === 'ar'
  // The pronunciation line runs in whichever script the model wrote it in:
  // Latin letters under a Hebrew result, Hebrew letters under a foreign one
  // (Hebrew -> English). Read the script off the text, not off the language.
  const transliterationRtl = /[\u0590-\u05FF]/.test(transliteration)
  const { open: openSignIn } = useSignIn()

  // "Reply in Hebrew" — only offered when the learner is reading a Hebrew
  // message someone sent them (he-to-other) and it is short enough to reply
  // to. Mirrors mobile's canDraftReplies (constants/replyDrafts.ts).
  const [replyDraftsOpen, setReplyDraftsOpen] = useState(false)
  const replyLanguageName = useMemo(() => LANGUAGES.find((l) => l.code === targetLang)?.name, [targetLang])
  const canDraftReply = useMemo(() => {
    if (tab !== 'text' || sourceLang !== 'he' || targetLang === 'he') return false
    if (!output || isTranslating || !!error) return false
    return isDraftableLength(sourceText)
  }, [tab, sourceLang, targetLang, output, isTranslating, error, sourceText])

  // Auto-copy is desktop-only (a touch browser refuses the write), so the
  // menu item only exists where the setting can actually do something.
  const [canAutoCopy, setCanAutoCopy] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer: fine)').matches,
  )
  useEffect(() => {
    const mql = window.matchMedia('(pointer: fine)')
    const onChange = () => setCanAutoCopy(mql.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  // Escape closes the copy-format menu, like every other menu on this pane.
  useEffect(() => {
    if (!showCopyMenu) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowCopyMenu(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [showCopyMenu, setShowCopyMenu])

  const handleOpenReplyDrafts = () => {
    if (!isAuthed) {
      openSignIn('library')
      return
    }
    setReplyDraftsOpen(true)
  }

  // A media translation lands in `mediaResult`, a text one in `output`. Every
  // "is there something to show" check below has to accept either, otherwise a
  // finished PDF still reads as an empty pane.
  const hasResult = !!output || !!mediaResult

  // Fetch the markdown chunk while the first result is still streaming, so the
  // switch from plain text at the end is immediate. A failure here is left to
  // the render path, which retries (and reloads on a stale deploy) itself.
  useEffect(() => {
    if (isTranslating) void import('./TranslatorMarkdown').catch(() => {})
  }, [isTranslating])

  const statusNode = error
    ? <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--err)' }} />failed</span>
    : isTranslating
      ? <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: 'var(--accent)' }} />{isSummarizing ? 'summarizing…' : 'translating…'}</span>
      : hasResult && translationElapsedMs > 0
        ? <span className="inline-flex items-center gap-1.5" style={{ color: 'var(--ok)' }}><span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--ok)' }} />done · {(translationElapsedMs / 1000).toFixed(1)} s</span>
        : null

  const outputBody = (compact: boolean) => (
    <div dir={targetRtl ? 'rtl' : 'ltr'} lang={targetLang}>
      <div
        className={cn('select-text', scaledFontSize(output.length))}
        style={{
          fontFamily: targetRtl ? 'var(--font-hebrew)' : 'var(--font-sans)',
          fontSize: compact ? 19 : 23,
          lineHeight: 1.55,
          color: 'var(--ink)',
        }}
      >
        {/* Streaming text is shown as it arrives; parsing markdown again on every
            chunk re-rendered the whole result dozens of times a second. */}
        {isTranslating ? (
          <PlainOutput text={output} />
        ) : (
          <Suspense fallback={<PlainOutput text={output} />}>
            <TranslatorMarkdown>{output}</TranslatorMarkdown>
          </Suspense>
        )}
        {isTranslating && <span className="inline-block ml-0.5 w-0.5 h-[1em] align-middle animate-pulse" style={{ background: 'var(--ink)' }} />}
      </div>
      {transliteration && (
        <div
          className={cn('pt-2', transliterationRtl ? '' : 'font-mono')}
          style={{
            fontSize: transliterationRtl ? 15 : 12,
            lineHeight: 1.6,
            color: 'var(--muted)',
            fontFamily: transliterationRtl ? 'var(--font-hebrew)' : undefined,
          }}
          dir={transliterationRtl ? 'rtl' : 'ltr'}
        >
          {transliteration}
        </div>
      )}
    </div>
  )

  /**
   * Copy lives at the top of the result, next to the text it copies — the
   * first thing anyone does with a translation should not be a trip to the
   * bottom of the pane. `menuBelow` flips the format menu downwards for that
   * header placement; the bar placement still opens upwards.
   */
  const copyControl = (menuBelow: boolean) => (
    <div className="relative flex items-center">
      {showCopyMenu && <div className="fixed inset-0 z-30" onClick={() => setShowCopyMenu(false)} />}
      {/* The buttons sit above the menu's own click-away backdrop, so the
        * chevron closes what it opened instead of being covered by it. */}
      <div className="relative z-40 flex items-center">
        <WsIconButton
          className="touch-44"
          icon={copied ? <Check size={15} /> : <Copy size={15} />}
          label={copied ? 'Copied' : 'Copy translation'}
          size={26}
          active={copied}
          onClick={() => onCopyFormatted('plain')}
        />
        <WsIconButton
          icon={<ChevronDown size={11} className={cn('transition-transform', showCopyMenu && 'rotate-180')} />}
          label="More copy options"
          size={18}
          className="touch-44-y"
          active={showCopyMenu}
          onClick={() => setShowCopyMenu((v) => !v)}
        />
      </div>
      {showCopyMenu && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 z-40 min-w-[220px] overflow-hidden rounded-md animate-scale-in',
            menuBelow ? 'top-full mt-1.5' : 'bottom-full mb-1.5',
          )}
          style={{
            border: '1px solid var(--line)',
            background: 'var(--card)',
            boxShadow: 'var(--shadow-lg)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {transliteration && (
            <button
              type="button"
              onClick={() => onCopyFormatted('with-translit')}
              className="w-full text-left px-3 py-2 text-[13px] font-medium transition-colors duration-100 hover:bg-[var(--card-2)] cursor-pointer"
              style={{ color: 'var(--ink)' }}
            >
              Copy with Translation + Transliteration
            </button>
          )}
          <button
            type="button"
            onClick={() => onCopyFormatted('markdown')}
            className="w-full text-left px-3 py-2 text-[13px] font-medium transition-colors duration-100 hover:bg-[var(--card-2)] cursor-pointer"
            style={{ color: 'var(--ink)', borderTop: transliteration ? '1px solid var(--line-hair)' : undefined }}
          >
            Copy as Markdown
          </button>
          {/* The setting belongs where the action is, not only three screens
            * away in Settings. Desktop only, because a touch browser refuses
            * a clipboard write this far from the tap. */}
          {onToggleAutoCopy && canAutoCopy && (
            <button
              type="button"
              role="menuitemcheckbox"
              aria-checked={!!autoCopy}
              onClick={() => onToggleAutoCopy(!autoCopy)}
              className="w-full flex items-center gap-2 text-left px-3 py-2 text-[13px] font-medium transition-colors duration-100 hover:bg-[var(--card-2)] cursor-pointer"
              style={{ color: 'var(--ink)', borderTop: '1px solid var(--line-hair)' }}
            >
              <span
                className="inline-flex items-center justify-center shrink-0"
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 3,
                  border: `1px solid ${autoCopy ? 'var(--accent)' : 'var(--line-2)'}`,
                  background: autoCopy ? 'var(--accent)' : 'transparent',
                  color: 'var(--cta-fg)',
                }}
                aria-hidden
              >
                {autoCopy && <Check size={10} strokeWidth={3} />}
              </span>
              Copy every translation automatically
            </button>
          )}
        </div>
      )}
    </div>
  )

  return (
    <div className="flex flex-1 min-w-0 flex-col h-full min-h-0 border-t md:border-t-0 md:border-l border-app max-md:border-t-0 max-md:h-auto" style={{ background: 'var(--card)' }}>
      <div className="flex items-center justify-between gap-2 min-h-[30px] shrink-0 px-[18px] pt-2.5 min-w-0">
        <span className="font-mono truncate min-w-0" style={{ fontSize: 11, letterSpacing: '0.06em', color: 'var(--muted-2)' }}>
          TARGET · {targetLang.toUpperCase()}
        </span>
        <div className="flex items-center gap-2 shrink-0 min-w-0">
          {statusNode && <span className="font-mono shrink-0 truncate" style={{ fontSize: 11, color: 'var(--muted-2)' }}>{statusNode}</span>}
          {hasResult && !isTranslating && copyControl(true)}
        </div>
      </div>

      {/* Phone: the result grows to its content and the whole column scrolls
        * (see Translator), so nothing inside the card is ever hidden behind
        * an inner scroll: the gender nudge and word alternatives stay in view. */}
      <div className="flex-1 min-h-0 overflow-y-auto px-[18px] pb-3 max-md:overflow-visible max-md:flex-none" role="region" aria-live="polite" aria-busy={isTranslating}>
        {isTranslating && !hasResult && (
          <div className="space-y-3 pt-1.5" role="status">
            <div className="baba-skeleton h-6 w-4/5" />
            <div className="baba-skeleton h-6 w-3/5" />
            <div className="baba-skeleton h-6 w-2/3" />
          </div>
        )}

        {limitKind && !isTranslating && <LimitCard kind={limitKind} onTryPro={onUpgradeClick} />}

        {!isTranslating && !hasResult && !limitKind && (
          <div className="flex-1 flex flex-col items-center justify-center h-full min-h-[220px] select-none gap-4 py-10 text-center">
            <div className="flex items-center justify-center w-10 h-10 rounded-full" style={{ background: 'var(--card-2)' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--muted-2)" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M7.5 12h.01M12 12h.01M16.5 12h.01M12 21a9 9 0 10-8.25-5.4L2 21l5.4-1.75A8.96 8.96 0 0012 21z" />
              </svg>
            </div>
            <div className="space-y-1 max-w-[260px]" lang="en" dir="ltr">
              <p className="text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>Your Translation Shows Up Here</p>
              <p className="text-[13px] leading-relaxed" style={{ color: 'var(--muted)' }}>
                {tab === 'file' ? (
                  <>
                    <span className="hidden md:inline">Add a file on the left, then press Translate File.</span>
                    <span className="md:hidden">Add a file above, then tap Translate.</span>
                  </>
                ) : (
                  <>
                    <span className="hidden md:inline">Start typing on the left, then press <span className="font-mono">⌘↵</span>.</span>
                    <span className="md:hidden">Type above, then tap Translate.</span>
                  </>
                )}
              </p>
            </div>
          </div>
        )}

        {/* One result at a time. A structured image/PDF document and a prose
          * `output` are two renderings of two different translations; showing
          * both is how a previous text result ended up under an image card. */}
        {mediaResult ? (
          <div className="pt-1.5">
            <MediaResultBody result={mediaResult} />
            {/* While translating, the card is a partial the gateway is still
              * writing; the finished one replaces it on the done frame. */}
            {isTranslating && (
              <div className="inline-flex items-center gap-1.5 pt-2 font-mono" style={{ fontSize: 11, color: 'var(--muted-2)' }} lang="en" dir="ltr" role="status">
                <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: 'var(--accent)' }} aria-hidden />
                still writing…
              </div>
            )}
          </div>
        ) : output && tab === 'file' && originalText ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1.5">
            <div className="md:border-r md:pr-4" style={{ borderColor: 'var(--line-hair)' }}>
              <div className="font-mono uppercase mb-1.5" style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--muted-2)' }} lang="en" dir="ltr">Original</div>
              <div dir={sourceRtl ? 'rtl' : 'auto'} lang="he" className="leading-relaxed whitespace-pre-wrap select-text" style={{ fontFamily: 'var(--font-hebrew)', fontSize: 15, color: 'var(--muted)' }}>
                {originalText}
              </div>
            </div>
            <div>
              <div className="font-mono uppercase mb-1.5" style={{ fontSize: 10.5, letterSpacing: '0.1em', color: 'var(--muted-2)' }} lang="en" dir="ltr">Translation</div>
              {outputBody(true)}
            </div>
          </div>
        ) : output ? (
          <div className="pt-1.5">{outputBody(false)}</div>
        ) : null}

        {/* Drafting a reply is not one more icon in the row of small verbs on
          * the result you already have (copy it, hear it, save it) — it
          * GENERATES something and spends a metered draft, so it sits under
          * the message it answers where it can actually be seen. Mirrors the
          * phone's foot-of-the-card placement in InlineResultPanel. */}
        {canDraftReply && (
          <div className="pt-3">
            <button
              type="button"
              onClick={handleOpenReplyDrafts}
              className="inline-flex items-center gap-1.5 rounded-md font-medium transition-all duration-150 hover:brightness-[0.98] active:scale-95 cursor-pointer touch-44-y"
              style={{
                height: 32,
                padding: '0 12px',
                fontSize: 13,
                color: 'var(--accent-deep)',
                background: 'var(--accent-soft)',
                border: '1px solid var(--accent-soft)',
              }}
            >
              <Sparkles size={13} aria-hidden />
              Reply in Hebrew
            </button>
          </div>
        )}

        {summary && !mediaResult && (output || isTranslating) && (
          <div className="mt-4 px-3.5 py-3" style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card-2)' }}>
            <div className="flex items-center gap-1.5 font-mono uppercase mb-1.5" style={{ fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--muted-2)' }} lang="en" dir="ltr">
              Key points
            </div>
            <div dir="auto" lang={targetLang} className="leading-relaxed text-[13px] select-text" style={{ color: 'var(--ink)' }}>
              {isTranslating ? (
                <PlainOutput text={summary} />
              ) : (
                <Suspense fallback={<PlainOutput text={summary} />}>
                  <TranslatorMarkdown>{summary}</TranslatorMarkdown>
                </Suspense>
              )}
            </div>
          </div>
        )}

        {(variationsLoading || variations.length > 0) && output && !isTranslating ? (
          <div className="mt-3 px-3 py-2.5" style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--bg)' }}>
            <div className="flex items-center justify-between px-1 pb-1.5">
              <span className="text-[12px] font-medium" style={{ color: 'var(--ink)' }}>Alternatives</span>
            </div>
            {variationsLoading ? (
              <div className="space-y-1.5 px-1">
                {[0, 1, 2].map((i) => <div key={i} className="baba-skeleton h-6 rounded" />)}
              </div>
            ) : (
              variations.map((v, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => onPickVariation?.(v)}
                  dir="auto"
                  lang={targetLang}
                  className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left transition-colors"
                  style={{ borderRadius: 4 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--card-2)' }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
                >
                  <span style={{ fontFamily: targetRtl ? 'var(--font-hebrew)' : 'var(--font-sans)', fontSize: 15, color: 'var(--ink)' }}>{v}</span>
                </button>
              ))
            )}
          </div>
        ) : null}

        {showGenderNudge && (
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-2 px-3 py-2 text-[12.5px]" style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card-2)', color: 'var(--ink)' }}>
            <Info size={13} className="shrink-0" style={{ color: 'var(--muted-2)' }} aria-hidden />
            <span className="flex-1 min-w-[180px]">Translated for general context. Need it for a specific person?</span>
            <div className="flex items-center gap-2 ml-auto">
              <WsButtonSecondary h={26} className="touch-44-y" onClick={onGenderWoman}>To a Woman</WsButtonSecondary>
              <WsButtonSecondary h={26} className="touch-44-y" onClick={onGenderMan}>To a Man</WsButtonSecondary>
              <WsIconButton icon={<X size={12} />} label="Dismiss" size={22} className="touch-44" onClick={onDismissGenderNudge} />
            </div>
          </div>
        )}

        {error && !limitKind ? (
          <div className="mt-3 flex items-start gap-3 px-3.5 py-2.5 text-[13px]" style={{ borderRadius: 6, border: '1px solid var(--err)', background: 'var(--card)', color: 'var(--err)' }}>
            <div className="flex-1">{error}</div>
            {limitReached ? (
              <WsButtonSecondary h={26} className="touch-44-y" onClick={onUpgradeClick}>Upgrade →</WsButtonSecondary>
            ) : (
              <WsButtonSecondary h={26} className="touch-44-y" onClick={onRetry}>Retry</WsButtonSecondary>
            )}
          </div>
        ) : null}

        {(reverseOutput || isReversing) && (
          <div className="mt-3" style={{ borderRadius: 6, border: '1px solid var(--line)', background: 'var(--card-2)' }}>
            <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--line-hair)' }}>
              <div className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: '0.08em', color: 'var(--muted-2)' }}>Reverse check</div>
              <WsIconButton icon={<X size={11} />} label="Dismiss" size={20} onClick={onClearReverse} />
            </div>
            <div className="max-h-[180px] overflow-y-auto px-3 py-2.5">
              <div dir="auto" className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--muted)' }}>
                {reverseOutput || (isReversing && <span style={{ color: 'var(--muted-2)' }}>Checking…</span>)}
                {isReversing && reverseOutput && <span className="inline-block ml-1 w-0.5 h-[1em] bg-current animate-pulse align-middle" />}
              </div>
            </div>
          </div>
        )}
      </div>

      {(hasResult || isTranslating) && (
        <div className="shrink-0 px-[18px] pt-3 pb-3 border-t" style={{ borderColor: 'var(--line-hair)' }}>
          {hasResult && !isTranslating ? (
            <div className="flex flex-col gap-2">
              {/* Wraps on a phone instead of running off the pane's right edge. */}
              <div className="flex flex-wrap items-center gap-0.5 min-w-0">
                {/* Generating a clip takes a beat. Without this the button
                  * looked inert for two seconds and people pressed it again. */}
                <WsIconButton
                  className="touch-44"
                  icon={isPreparingSpeech ? <Loader2 size={17} className="animate-spin" /> : <Volume2 size={17} />}
                  label={isPreparingSpeech ? 'Getting the audio…' : isSpeaking ? 'Stop' : 'Listen'}
                  size={32}
                  active={isSpeaking || !!isPreparingSpeech}
                  onClick={onSpeak}
                />
                {isPreparingSpeech && (
                  <span className="font-mono ml-0.5 mr-1 animate-fade-in" style={{ fontSize: 11, color: 'var(--muted-2)' }}>
                    getting audio…
                  </span>
                )}
                <WsIconButton className="touch-44" icon={<Share2 size={17} />} label="Share link" size={32} onClick={onShareLink} />
                {/* Favorites are text rows keyed to the source text, and a
                  * media translation has none — saving one would write a
                  * second, sourceless row beside the image/PDF row the ledger
                  * already holds. The Library's own Save on that row is the
                  * right control for this. */}
                {!mediaResult && (
                  <WsIconButton className="touch-44" icon={<Bookmark size={17} fill={saved ? 'currentColor' : 'none'} />} label={saved ? 'Saved — remove from Library' : 'Save to Library'} size={32} active={saved} onClick={onFavorite} />
                )}

                <MoreMenu
                  items={[
                    ...(!!output && output.length <= 150 && onGenerateVariations
                      ? [{ label: variationsLoading ? 'Generating…' : 'Variations', hint: 'Other ways to say it', badge: isProLoaded && !isPro ? 'PRO' : undefined, onClick: onGenerateVariations }]
                      : []),
                    ...(tab === 'text' && mode !== 'summary' && canSummarizeText && onSummarizeText
                      ? [{ label: 'Summarize', hint: 'Main points only', badge: isProLoaded && !isPro ? 'PRO' : undefined, onClick: onSummarizeText }]
                      : []),
                    { label: 'Check Translation', hint: 'Translate it back to compare', onClick: onReverseTranslate },
                    ...(tab === 'text' && retryCount < 3
                      ? [{ label: `Try Again${retryCount > 0 ? ` (${3 - retryCount} left)` : ''}`, hint: 'Get a different translation', onClick: onRetryWithContext }]
                      : []),
                  ]}
                />

                <div className="hidden md:block flex-1" />
                {/* Phones have Translate Again pinned in the bottom bar. */}
                <WsButtonSecondary h={32} className="touch-44-y text-[13.5px] hidden md:inline-flex" onClick={onRetry}>Retranslate</WsButtonSecondary>
              </div>

              {/* Phones get the smart banner / download sheet (MobileAppPromo) instead. */}
              <div className="hidden md:block">
                <GetOnPhoneNudge isAuthed={isAuthed} />
              </div>
            </div>
          ) : isTranslating ? (
            <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 text-[12px] transition-colors" style={{ color: 'var(--muted)' }}>
              <X size={11} aria-hidden /> Cancel
            </button>
          ) : null}
        </div>
      )}

      <ReplyDraftsSheet
        open={canDraftReply && replyDraftsOpen}
        onClose={() => setReplyDraftsOpen(false)}
        sourceText={sourceText}
        translatedText={output}
        language={targetLang}
        languageNative={replyLanguageName}
        genderContext={speakingTo}
        userGender={selfGender}
      />
    </div>
  )
}
